import { useMemo, useState, type ChangeEvent, type FormEvent, type ReactNode } from 'react'
import { useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Building2, Mail, MapPin, Phone } from 'lucide-react'
import type { ContactCreate } from '@brochure/shared'
import { CatalogMetadata } from '@/components/catalog'
import { LocalizedLink } from '@/components/LocalizedLink'
import { publicProducts } from '@/data/public-catalog'
import { getLocalizedPath, languageFromPath } from '@/i18n/routes'
import { apiUrl } from '@/lib/api'

type FieldName = keyof ContactCreate
type FieldErrors = Partial<Record<FieldName, string>>
type SubmitState = 'idle' | 'pending' | 'success' | 'error'
type ContactLocationState = { productId?: string; subject?: string; message?: string } | null
type ContactFormState = Omit<ContactCreate, 'privacyConsent'> & { privacyConsent: boolean }

const initialForm = (subject: string, message: string): ContactFormState => ({
  name: '',
  email: '',
  phone: '',
  subject,
  message,
  privacyConsent: false,
})

const isContactSuccess = (value: unknown): value is { success: true; message: string } =>
  typeof value === 'object' && value !== null &&
  (value as { success?: unknown }).success === true &&
  typeof (value as { message?: unknown }).message === 'string' &&
  (value as { message: string }).message.length > 0

export function ContactPage() {
  const { t } = useTranslation('common')
  const location = useLocation()
  const language = languageFromPath(location.pathname)
  const state = location.state as ContactLocationState
  const context = useMemo(() => {
    const query = new URLSearchParams(location.search)
    const productId = query.get('product') ?? state?.productId ?? ''
    const productRecord = publicProducts.find((item) => item.id === productId)
    const product = (productRecord?.languages[language]?.title ?? productRecord?.languages.it?.title ?? productRecord?.languages.en?.title ?? '').slice(0, 120)
    const subject = (query.get('subject') ?? state?.subject ?? '').slice(0, 150)
    const message = (query.get('message') ?? state?.message ?? '').slice(0, 4000)
    return { product, subject, message }
  }, [language, location.search, state?.message, state?.productId, state?.subject])
  const [form, setForm] = useState<ContactFormState>(() => initialForm(context.subject, context.message))
  const [errors, setErrors] = useState<FieldErrors>({})
  const [submitState, setSubmitState] = useState<SubmitState>('idle')

  const fieldError = (name: FieldName, value: ContactFormState[FieldName]) => {
    if (name === 'name' && (typeof value !== 'string' || value.trim().length < 2 || value.length > 100)) return t('contacts.form.errors.name')
    if (name === 'email' && (typeof value !== 'string' || !/^\S+@\S+\.\S+$/.test(value) || value.length > 254)) return t('contacts.form.errors.email')
    if (name === 'phone' && typeof value === 'string' && value.length > 50) return t('contacts.form.errors.phone')
    if (name === 'subject' && typeof value === 'string' && value.length > 150) return t('contacts.form.errors.subject')
    if (name === 'message' && (typeof value !== 'string' || value.trim().length < 10 || value.length > 4000)) return t('contacts.form.errors.message')
    if (name === 'privacyConsent' && value !== true) return t('contacts.form.errors.privacy')
    return undefined
  }

  const validate = () => {
    const next: FieldErrors = {}
    ;(['name', 'email', 'phone', 'subject', 'message', 'privacyConsent'] as FieldName[]).forEach((name) => {
      const error = fieldError(name, form[name])
      if (error) next[name] = error
    })
    setErrors(next)
    return Object.keys(next).length === 0
  }

  const change = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = event.target
    const nextValue = type === 'checkbox' ? (event.target as HTMLInputElement).checked : value
    setForm((current) => ({ ...current, [name]: nextValue }))
    setErrors((current) => ({ ...current, [name]: undefined }))
    if (submitState !== 'idle') setSubmitState('idle')
  }

  const blur = (name: FieldName) => setErrors((current) => ({ ...current, [name]: fieldError(name, form[name]) }))

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (!validate()) return
    setSubmitState('pending')
    try {
      const response = await fetch(apiUrl('/api/contact'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      })
      const body: unknown = await response.json().catch(() => null)
      if (!response.ok || !isContactSuccess(body)) throw new Error('invalid-contact-response')
      setSubmitState('success')
    } catch {
      setSubmitState('error')
    }
  }

  const statusText = submitState === 'success'
    ? t('contacts.form.success')
    : submitState === 'error'
      ? t('contacts.form.providerError')
      : ''

  return <div className="contact-page">
    <CatalogMetadata
      language={language}
      title={t('contacts.title')}
      description={t('contacts.metaDescription')}
      canonicalPath={getLocalizedPath('contacts', language)}
      alternatePaths={{ it: getLocalizedPath('contacts', 'it'), en: getLocalizedPath('contacts', 'en') }}
    />
    <header className="contact-hero internal-hero"><div className="shell-container">
      <nav className="catalog-breadcrumb" aria-label={t('contacts.breadcrumbLabel')}><ol><li><LocalizedLink to="home">{t('nav.home')}</LocalizedLink></li><li aria-current="page"><span>/</span><span>{t('contacts.title')}</span></li></ol></nav>
      <div className="contact-heading"><h1 tabIndex={-1}>{t('contacts.title')}</h1><p>{t('contacts.intro')}</p></div>
    </div></header>
    <main className="shell-container contact-layout">
      <aside className="contact-details" aria-labelledby="contact-details-title">
        <h2 id="contact-details-title">{t('contacts.detailsTitle')}</h2>
        <div className="contact-office"><Building2 aria-hidden="true" /><div><h3>{t('contacts.legalOffice')}</h3><address>{t('contacts.legalAddress')}<br />{t('contacts.legalCity')}</address></div></div>
        <div className="contact-office"><MapPin aria-hidden="true" /><div><h3>{t('contacts.operatingOffice')}</h3><address>{t('contact.address')}<br />{t('contact.city')}</address></div></div>
        <div className="contact-direct"><Phone aria-hidden="true" /><a href="tel:+390490995400">{t('contact.phone')}</a></div>
        <div className="contact-direct"><Mail aria-hidden="true" /><a href="mailto:esamed@esamed.it">{t('contact.email')}</a></div>
        <p className="contact-vat">{t('footer.vat')}</p>
      </aside>
      <section className="contact-form-panel" aria-labelledby="contact-form-title">
        <h2 id="contact-form-title">{t('contacts.form.title')}</h2>
        <p>{t('contacts.form.intro')}</p>
        {context.product && <p className="contact-context" data-testid="contact-context">{t('contacts.form.productContext', { product: context.product })}</p>}
        <form noValidate onSubmit={submit}>
          <div className="contact-form-grid">
            <Field name="name" label={t('contacts.form.name')} required error={errors.name}><input id="contact-name" name="name" value={form.name} maxLength={100} onChange={change} onBlur={() => blur('name')} autoComplete="name" aria-invalid={Boolean(errors.name)} aria-describedby={errors.name ? 'contact-name-error' : undefined} /></Field>
            <Field name="email" label={t('contacts.form.email')} required error={errors.email}><input id="contact-email" name="email" type="email" value={form.email} onChange={change} onBlur={() => blur('email')} autoComplete="email" aria-invalid={Boolean(errors.email)} aria-describedby={errors.email ? 'contact-email-error' : undefined} /></Field>
            <Field name="phone" label={t('contacts.form.phone')} error={errors.phone}><input id="contact-phone" name="phone" type="tel" value={form.phone ?? ''} onChange={change} onBlur={() => blur('phone')} autoComplete="tel" aria-invalid={Boolean(errors.phone)} aria-describedby={errors.phone ? 'contact-phone-error' : undefined} /></Field>
            <Field name="subject" label={t('contacts.form.subject')} error={errors.subject}><input id="contact-subject" name="subject" value={form.subject ?? ''} maxLength={150} onChange={change} onBlur={() => blur('subject')} aria-invalid={Boolean(errors.subject)} aria-describedby={errors.subject ? 'contact-subject-error' : undefined} /></Field>
          </div>
          <Field name="message" label={t('contacts.form.message')} required error={errors.message}><textarea id="contact-message" name="message" rows={7} maxLength={4000} value={form.message} onChange={change} onBlur={() => blur('message')} aria-invalid={Boolean(errors.message)} aria-describedby={errors.message ? 'contact-message-error' : 'contact-message-help'} /><span id="contact-message-help" className="field-help">{t('contacts.form.messageHelp')}</span></Field>
          <div className="privacy-field">
            <input id="contact-privacy" name="privacyConsent" type="checkbox" checked={form.privacyConsent === true} onChange={change} onBlur={() => blur('privacyConsent')} aria-invalid={Boolean(errors.privacyConsent)} aria-describedby={errors.privacyConsent ? 'contact-privacy-error' : undefined} />
            <label htmlFor="contact-privacy">{t('contacts.form.privacy')} <LocalizedLink to="privacy">{t('contacts.form.privacyLink')}</LocalizedLink></label>
          </div>
          {errors.privacyConsent && <p id="contact-privacy-error" className="field-error">{errors.privacyConsent}</p>}
          <div className="contact-submit-row"><button className="button" type="submit" disabled={submitState === 'pending'}>{submitState === 'pending' ? t('contacts.form.sending') : t('contacts.form.send')}</button><p className={`contact-status${submitState === 'error' ? ' contact-status--error' : ''}`} role="status" aria-live="polite">{statusText}</p></div>
        </form>
      </section>
    </main>
    <section className="contact-map" aria-label={t('contacts.map.title')}>
      <iframe
        title={t('contacts.map.title')}
        src="https://www.google.com/maps?q=Via%20Spagna%2013%2FA%2C%2035010%20Vigonza%20PD%2C%20Italy&output=embed"
        loading="lazy"
        referrerPolicy="no-referrer-when-downgrade"
      />
    </section>
  </div>
}

function Field({ name, label, error, required = false, children }: { name: string; label: string; error?: string; required?: boolean; children: ReactNode }) {
  return <div className={`form-field form-field--${name}`}><label htmlFor={`contact-${name}`}>{label}{required && <span aria-hidden="true"> *</span>}</label>{children}{error && <p id={`contact-${name}-error`} className="field-error">{error}</p>}</div>
}
